import React, { useEffect, useMemo, useState, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { Context } from '../../../index';
import Sidebar from './sidebar';
import './Apply.css';

const KZT = new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 2 });
const RU_DATE = new Intl.DateTimeFormat('ru-RU');

const API = process.env.REACT_APP_API_URL;
const HEADERS = { 'Content-Type': 'application/json' };

const Apply = observer(() => {
  const { user } = useContext(Context);
  const isStaff = user.isStaff;
  const activeClientId = user?.activeClient?.id ?? null;
  // Все клиенты, привязанные к пользователю (для роли 3, когда конкретный не выбран).
  const allowedClientIds = useMemo(
    () => (user?.clients || []).map((c) => Number(c.id)).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.clients?.length]
  );
  const [agreements, setAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState('');
  const [orderItems, setOrderItems] = useState([]);
  const [orderQuantities, setOrderQuantities] = useState({});
  const [selectedDate, setSelectedDate] = useState('');
  const [comment, setComment] = useState('');
  const [clientName, setClientName] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [organization, setOrganization] = useState(null);

  const [loadingAgreements, setLoadingAgreements] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const location = useLocation();

  // ---- init: ?contract=<id>
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const fromQuery = q.get('contract');
    if (fromQuery) setSelectedAgreement(String(fromQuery));
  }, [location.search]);

  // ---- fetch: org + agreements
  useEffect(() => {
    (async () => {
      try {
        setLoadingAgreements(true);
        const [orgResp, agrResp] = await Promise.all([
          fetch(`${API}v1/organizations`, { headers: HEADERS }),
          fetch(`${API}v1/contracts`, { headers: HEADERS }),
        ]);
        const orgData = await orgResp.json();
        if (Array.isArray(orgData) && orgData.length) setOrganization(orgData[0]);

        const agrData = await agrResp.json();
        if (Array.isArray(agrData)) {
          // Скоуп договоров для клиент-роли:
          //  - выбрана конкретная организация → только её договоры;
          //  - «Все мои организации» → договоры всех клиентов пользователя;
          //  - роль admin/operator → видим все договоры (фильтр не применяем).
          let filtered = agrData;
          if (!isStaff) {
            if (activeClientId) {
              filtered = agrData.filter((a) => Number(a.id_client) === Number(activeClientId));
            } else if (allowedClientIds.length) {
              const allowSet = new Set(allowedClientIds);
              filtered = agrData.filter((a) => allowSet.has(Number(a.id_client)));
            } else {
              filtered = []; // у клиента нет привязок — нет договоров
            }
          }
          setAgreements(filtered);
          // если pre-selected contract (?contract=N) не в списке — очищаем выбор
          setSelectedAgreement((prev) =>
            prev && !filtered.some((a) => String(a.id) === String(prev)) ? '' : prev
          );
        }
      } catch (e) {
        console.error(e);
        setErrorMsg('Не удалось загрузить договоры. Попробуйте обновить страницу.');
      } finally {
        setLoadingAgreements(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, activeClientId, allowedClientIds.length]);

  // ---- при выборе договора: тянем товары + клиента
  useEffect(() => {
    if (!selectedAgreement) {
      setOrderItems([]);
      setOrderQuantities({});
      setClientName('');
      setDeliveryAddress('');
      return;
    }
    (async () => {
      try {
        setLoadingItems(true);
        setErrorMsg('');
        const agr = agreements.find((a) => Number(a.id) === Number(selectedAgreement));
        const clientId = agr?.id_client;
        if (clientId) {
          const resp = await fetch(`${API}v1/clients/${clientId}`, { headers: HEADERS });
          const data = await resp.json();
          if (data?.name) {
            setClientName(data.name);
            setDeliveryAddress(data.address || '');
          }
        }
        const itemsResp = await fetch(
          `${API}v1/contracts_view/${selectedAgreement}`,
          { headers: HEADERS }
        );
        const rawItems = await itemsResp.json();
        if (!Array.isArray(rawItems)) {
          setOrderItems([]); setOrderQuantities({}); return;
        }
        const withQty = await Promise.all(
          rawItems.map(async (it) => {
            const qResp = await fetch(`${API}v1/contractitems/${it.id_contractitem}`, { headers: HEADERS });
            const qData = await qResp.json();
            return { ...it, quantity: Number(qData?.quantity ?? 0) };
          })
        );
        setOrderItems(withQty);
        const zeroed = {};
        withQty.forEach((it) => { zeroed[it.id_contractitem] = 0; });
        setOrderQuantities(zeroed);
      } catch (e) {
        console.error(e);
        setErrorMsg('Не удалось загрузить товары по договору.');
      } finally {
        setLoadingItems(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgreement, agreements]);

  const filteredItems = useMemo(
    () => orderItems.filter((it) => Number(it.id_contract) === Number(selectedAgreement)),
    [orderItems, selectedAgreement]
  );

  const totalAmount = useMemo(() => {
    return filteredItems.reduce((sum, it) => {
      const q = Number(orderQuantities[it.id_contractitem] || 0);
      return sum + q * Number(it.product_price || 0);
    }, 0);
  }, [filteredItems, orderQuantities]);

  const itemsCount = useMemo(
    () => Object.values(orderQuantities).filter((q) => Number(q) > 0).length,
    [orderQuantities]
  );

  const minDate = useMemo(() => {
    // Доставка можно с послезавтра — сегодня и завтра нельзя.
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 10);
  }, []);

  const canSubmit = selectedAgreement && selectedDate && itemsCount > 0 && !submitting;

  const stepQty = (id, delta, max) => {
    setOrderQuantities((prev) => {
      const cur = Number(prev[id] || 0);
      let next = cur + delta;
      if (next < 0) next = 0;
      if (max !== undefined && next > max) next = max;
      return { ...prev, [id]: next };
    });
  };
  const setQty = (id, raw, max) => {
    let n = Number(raw);
    if (!Number.isFinite(n) || n < 0) n = 0;
    if (max !== undefined && n > max) n = max;
    setOrderQuantities((prev) => ({ ...prev, [id]: n }));
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    if (!value) return setSelectedDate('');
    const d = new Date(value + 'T00:00:00Z');
    const day = d.getUTCDay();
    if (day === 0 || day === 6) {
      alert('Пожалуйста, выберите рабочий день (Пн–Пт).');
      return;
    }
    setSelectedDate(value);
  };

  // === Submit ===
  const submitOrderItems = async (orderId) => {
    const data = filteredItems
      .filter((it) => Number(orderQuantities[it.id_contractitem]) > 0)
      .map((it) => ({
        id_product: it.id_product,
        quantity: Number(orderQuantities[it.id_contractitem]),
        id_measure: it.id_measure,
        price: Number(it.product_price),
        total: Number(it.product_price) * Number(orderQuantities[it.id_contractitem]),
        id_order: orderId,
      }));
    if (!data.length) throw new Error('Нет позиций для отправки.');
    await Promise.all(
      data.map(async (payload) => {
        const r = await fetch(`${API}v1/orderitems`, {
          method: 'POST', headers: HEADERS, body: JSON.stringify(payload),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err?.error || 'Ошибка при создании позиции заказа');
        }
      })
    );
  };

  const updateContractItemsQuantities = async () => {
    const payloads = filteredItems.map((it) => ({
      id_contractitem: it.id_contractitem,
      id_product: it.id_product,
      id_contracts: it.id_contract,
      id_measure: it.id_measure,
      price: Number(it.product_price),
      quantity: Math.max(0, Number(it.quantity) - Number(orderQuantities[it.id_contractitem] || 0)),
      balance: it.product_balance,
      amount: it.product_amount,
    }));
    await Promise.all(
      payloads.map(async (p) => {
        const r = await fetch(`${API}v1/contractitems/${p.id_contractitem}`, {
          method: 'PUT', headers: HEADERS, body: JSON.stringify(p),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err?.error || 'Ошибка обновления остатков по договору');
        }
      })
    );
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setErrorMsg('');
      setSuccessMsg('');

      if (!canSubmit) {
        setErrorMsg('Заполните: договор, дата доставки, хотя бы один товар.');
        return;
      }
      const id_user = Number(user?.user?.id) || null;
      const agr = agreements.find((a) => Number(a.id) === Number(selectedAgreement));
      const id_client = agr?.id_client ?? null;

      const orderData = {
        order_date: new Date().toISOString().slice(0, 10),
        delivery_address: deliveryAddress || '',
        delivery_date: selectedDate,
        id_status: 1,
        id_payment_status: 1,
        total_amount: totalAmount,
        id_client, id_user,
        id_organization: organization?.id ?? null,
        client_commentary: comment || null,
        id_contracts: selectedAgreement,
        id_region: 2,
      };

      const resp = await fetch(`${API}v1/orders`, {
        method: 'POST', headers: HEADERS, body: JSON.stringify(orderData),
      });
      const created = await resp.json();
      if (!resp.ok || created?.error) {
        throw new Error(created?.error || 'Не удалось создать заявку');
      }
      const orderId = created.id;
      await submitOrderItems(orderId);
      await updateContractItemsQuantities();

      setSuccessMsg(`Заявка №${created.order_number || orderId} успешно отправлена.`);
      setOrderQuantities({});
      setComment('');
      setSelectedDate('');
    } catch (e) {
      console.error(e);
      setErrorMsg(e.message || 'Произошла ошибка при отправке.');
    } finally {
      setSubmitting(false);
    }
  };

  // === UI ===
  const agreementLabel = (a) => {
    const num = a.contract_number ? `№${a.contract_number}` : `№${a.id}`;
    const date = a.contract_date ? ` от ${RU_DATE.format(new Date(a.contract_date))}` : '';
    return num + date;
  };

  return (
    <div className="apply-container">
      <div className="apply-sidebar"><Sidebar /></div>

      <div className="apply-content">
        <div className="apply-header-row">
          <div>
            <h1 className="apply-h1">Подать заявку</h1>
            <p className="apply-sub">Выберите договор и укажите количество товаров.</p>
          </div>
        </div>

        {!!errorMsg && <div className="apply-alert apply-alert--error">{errorMsg}</div>}
        {!!successMsg && <div className="apply-alert apply-alert--ok">{successMsg}</div>}

        <section className="apply-card">
          <div className="apply-row">
            <label className="apply-field" htmlFor="apply-agr">
              <span className="apply-label">Договор</span>
              <select
                id="apply-agr"
                className="apply-input apply-select-native"
                value={selectedAgreement}
                onChange={(e) => setSelectedAgreement(e.target.value)}
                disabled={loadingAgreements}
              >
                <option value="">{loadingAgreements ? 'Загрузка…' : '— выберите —'}</option>
                {agreements.map((a) => (
                  <option key={a.id} value={a.id}>{agreementLabel(a)}</option>
                ))}
              </select>
            </label>

            <label className="apply-field" htmlFor="apply-date">
              <span className="apply-label">Дата доставки</span>
              <input
                id="apply-date"
                type="date"
                className="apply-input"
                value={selectedDate}
                min={minDate}
                onChange={handleDateChange}
              />
            </label>
          </div>

          <div className="apply-row apply-row--readonly">
            <div className="apply-field">
              <span className="apply-label">Клиент</span>
              <div className="apply-readonly">{clientName || '—'}</div>
            </div>
            <div className="apply-field">
              <span className="apply-label">Адрес доставки</span>
              <div className="apply-readonly">{deliveryAddress || '—'}</div>
            </div>
          </div>
        </section>

        <section className="apply-card">
          <div className="apply-card-head">
            <h2 className="apply-card-title">Товары по договору</h2>
            <div className="apply-card-meta">
              {loadingItems
                ? 'Загрузка…'
                : selectedAgreement
                  ? `${filteredItems.length} позиций`
                  : 'Сначала выберите договор'}
            </div>
          </div>

          {filteredItems.length === 0 ? (
            <div className="apply-empty">
              <div className="apply-empty-icon">📋</div>
              <div className="apply-empty-text">
                {selectedAgreement ? 'По выбранному договору нет позиций.' : 'Выберите договор, чтобы увидеть товары.'}
              </div>
            </div>
          ) : (
            <div className="apply-table-wrap">
              <table className="apply-products">
                <thead>
                  <tr>
                    <th className="apply-col-name">Название товара</th>
                    <th className="apply-col-unit">Ед.</th>
                    <th className="apply-col-num">Цена</th>
                    <th className="apply-col-num">Остаток</th>
                    <th className="apply-col-qty">Количество</th>
                    <th className="apply-col-num apply-col-sum">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((it) => {
                    const q = Number(orderQuantities[it.id_contractitem] || 0);
                    const max = Number(it.quantity || 0);
                    const sum = q * Number(it.product_price || 0);
                    const outOfStock = max === 0;
                    return (
                      <tr key={it.id_contractitem} className={outOfStock ? 'is-disabled' : ''}>
                        <td className="apply-col-name">{it.product_name}</td>
                        <td className="apply-col-unit">{it.measure_name}</td>
                        <td className="apply-col-num">{KZT.format(it.product_price)} ₸</td>
                        <td className={`apply-col-num ${outOfStock ? 'is-danger' : ''}`}>
                          {KZT.format(max)}
                        </td>
                        <td className="apply-col-qty">
                          <div className="qty-stepper">
                            <button
                              type="button"
                              className="qty-btn"
                              onClick={() => stepQty(it.id_contractitem, -1, max)}
                              disabled={q <= 0 || outOfStock}
                              aria-label="Уменьшить"
                            >−</button>
                            <input
                              type="number"
                              min="0"
                              max={max}
                              className="qty-input"
                              value={q}
                              onChange={(e) => setQty(it.id_contractitem, e.target.value, max)}
                              disabled={outOfStock}
                            />
                            <button
                              type="button"
                              className="qty-btn"
                              onClick={() => stepQty(it.id_contractitem, +1, max)}
                              disabled={q >= max || outOfStock}
                              aria-label="Увеличить"
                            >+</button>
                          </div>
                        </td>
                        <td className="apply-col-num apply-col-sum">
                          {sum > 0 ? `${KZT.format(sum)} ₸` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="apply-card">
          <label className="apply-field" htmlFor="apply-comment">
            <span className="apply-label">Комментарий <span className="apply-optional">(необязательно)</span></span>
            <textarea
              id="apply-comment"
              className="apply-input apply-textarea"
              placeholder="Уточнения по заявке, особенности доставки и т. п."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
          </label>
        </section>

        <div className="apply-footer">
          <div className="apply-summary">
            <div className="apply-summary-row">
              <span className="apply-summary-key">Позиций:</span>
              <span className="apply-summary-val">{itemsCount}</span>
            </div>
            <div className="apply-summary-row apply-summary-total">
              <span className="apply-summary-key">Итого:</span>
              <span className="apply-summary-val">{KZT.format(totalAmount)} ₸</span>
            </div>
          </div>
          <button
            type="button"
            className="apply-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
            title={!canSubmit ? 'Выберите договор, дату и количество товаров' : 'Отправить заявку'}
          >
            {submitting ? 'Отправка…' : 'Отправить заявку'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default Apply;
